/**
 * RTCPeerConnection helpers — ICE queue, polite peer, TURN-aware.
 */

export const DEFAULT_ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

export const DEFAULT_VIDEO_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30 },
};

export function loadIceServers() {
  const raw = process.env.REACT_APP_ICE_SERVERS_JSON;
  if (!raw) return DEFAULT_ICE_SERVERS;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch (e) {
    console.warn("Invalid REACT_APP_ICE_SERVERS_JSON", e);
  }
  return DEFAULT_ICE_SERVERS;
}

export const AUDIO_BITRATE_OPTIONS_KBPS = [32, 64, 96, 128, 256];
export const DEFAULT_AUDIO_BITRATE_KBPS = 128;
export const VIDEO_BITRATE_OPTIONS_KBPS = [500, 1000, 1500, 2500, 4000];
export const DEFAULT_VIDEO_BITRATE_KBPS = 1500;

/** Inject / update Opus maxaveragebitrate (bps) in SDP. */
export function mungeOpusMaxAvgBitrate(sdp, kbps) {
  if (!sdp || !kbps) return sdp;
  const bps = Math.round(Number(kbps) * 1000);
  if (!Number.isFinite(bps) || bps <= 0) return sdp;
  try {
    const rtpmap = sdp.match(/^a=rtpmap:(\d+)\s+opus\/\d+/mi);
    if (!rtpmap) return sdp;
    const pt = rtpmap[1];
    const fmtpRe = new RegExp(`^a=fmtp:${pt} .*$`, "mi");
    if (fmtpRe.test(sdp)) {
      return sdp.replace(fmtpRe, (line) => {
        if (/maxaveragebitrate=\d+/i.test(line)) {
          return line.replace(/maxaveragebitrate=\d+/i, `maxaveragebitrate=${bps}`);
        }
        return `${line};maxaveragebitrate=${bps}`;
      });
    }
    const insertAfter = new RegExp(`^a=rtpmap:${pt}\\s+opus\\/\\d+.*$`, "mi");
    return sdp.replace(
      insertAfter,
      (line) => `${line}\r\na=fmtp:${pt} maxaveragebitrate=${bps}`
    );
  } catch {
    return sdp;
  }
}

/**
 * Compute send/recv kbps for audio+video from consecutive getStats samples.
 * Mutates `lastStats` ({ sent: {}, recv: {} }) between calls.
 */
export function sampleMediaRates(statsReport, lastStats, now = Date.now()) {
  const agg = {
    audio: { send: 0, recv: 0 },
    video: { send: 0, recv: 0 },
  };
  if (!lastStats.sent) lastStats.sent = {};
  if (!lastStats.recv) lastStats.recv = {};

  statsReport.forEach((r) => {
    if (r.type === "outbound-rtp" && !r.isRemote) {
      const kind = r.kind || r.mediaType;
      if (kind !== "audio" && kind !== "video") return;
      const prev = lastStats.sent[r.id];
      if (prev && now > prev.ts && r.bytesSent >= prev.bytes) {
        const bps = (8 * (r.bytesSent - prev.bytes)) / ((now - prev.ts) / 1000);
        agg[kind].send += Math.max(0, Math.round(bps / 1000));
      }
      lastStats.sent[r.id] = { ts: now, bytes: r.bytesSent ?? 0 };
    }
    if (r.type === "inbound-rtp" && !r.isRemote) {
      const kind = r.kind || r.mediaType;
      if (kind !== "audio" && kind !== "video") return;
      const prev = lastStats.recv[r.id];
      if (prev && now > prev.ts && r.bytesReceived >= prev.bytes) {
        const bps =
          (8 * (r.bytesReceived - prev.bytes)) / ((now - prev.ts) / 1000);
        agg[kind].recv += Math.max(0, Math.round(bps / 1000));
      }
      lastStats.recv[r.id] = { ts: now, bytes: r.bytesReceived ?? 0 };
    }
  });
  return agg;
}

/**
 * Mutable session object holding PC / media state for one room session.
 * @param {{ isPolite: () => boolean, audioBitrateKbps?: number, videoBitrateKbps?: number }} opts
 */
export function createPeerSession({
  iceServers = DEFAULT_ICE_SERVERS,
  getRemotePeerId,
  getSelfId,
  sendSignal,
  onRemoteStream,
  onConnectionState,
  log = () => {},
  role = "client",
  isPolite = () => false,
  audioBitrateKbps = DEFAULT_AUDIO_BITRATE_KBPS,
  videoBitrateKbps = DEFAULT_VIDEO_BITRATE_KBPS,
}) {
  let pc = null;
  let dc = null;
  let localStream = null;
  let pendingCandidates = [];
  let remoteDescSet = false;
  let makingOffer = false;
  let currentAudioKbps = Number(audioBitrateKbps) || DEFAULT_AUDIO_BITRATE_KBPS;
  let currentVideoKbps = Number(videoBitrateKbps) || DEFAULT_VIDEO_BITRATE_KBPS;

  function isCameraOn() {
    return !!(
      localStream &&
      localStream
        .getVideoTracks()
        .some((t) => t.readyState === "live" && t.enabled)
    );
  }

  function isMicLive() {
    return !!(
      localStream &&
      localStream.getAudioTracks().some((t) => t.readyState === "live" && t.enabled)
    );
  }

  function isPeerActive() {
    return !!(pc && pc.connectionState && pc.connectionState !== "closed");
  }

  async function flushCandidates() {
    if (!pc || !remoteDescSet) return;
    const queued = pendingCandidates.splice(0, pendingCandidates.length);
    for (const c of queued) {
      try {
        await pc.addIceCandidate(c);
      } catch (e) {
        log("flush addIceCandidate error", e.toString());
      }
    }
  }

  async function ensurePC() {
    if (pc) return pc;
    pc = new RTCPeerConnection({ iceServers });
    remoteDescSet = false;
    pendingCandidates = [];

    pc.onicecandidate = (e) => {
      const remotePeerId = getRemotePeerId();
      if (e.candidate && remotePeerId) {
        sendSignal(remotePeerId, { type: "candidate", candidate: e.candidate });
      }
    };
    pc.ontrack = (ev) => {
      if (ev.streams?.[0]) onRemoteStream?.(ev.streams[0]);
    };
    pc.onconnectionstatechange = () => {
      log("pc state:", pc.connectionState);
      onConnectionState?.(pc.connectionState, pc.iceConnectionState);
    };
    pc.oniceconnectionstatechange = () => {
      onConnectionState?.(pc.connectionState, pc.iceConnectionState);
    };
    pc.ondatachannel = (ev) => {
      ev.channel.onmessage = (e) => log("DC:", e.data);
    };

    try {
      pc.addTransceiver("audio", { direction: "sendrecv" });
      pc.addTransceiver("video", { direction: "sendrecv" });
    } catch (e) {
      log("addTransceiver note:", e?.message || e);
    }

    if (role === "stage") {
      dc = pc.createDataChannel("chat");
      dc.onopen = () => log("dc open");
      dc.onmessage = (e) => log("dc msg:", e.data);
    }
    if (localStream) {
      localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));
    }
    return pc;
  }

  async function ensureMic(localVideoEl) {
    if (!localStream) localStream = new MediaStream();
    if (localStream.getAudioTracks().length === 0) {
      try {
        const mic = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStream.addTrack(mic.getAudioTracks()[0]);
        if (pc) pc.addTrack(mic.getAudioTracks()[0], localStream);
        if (localVideoEl && !localVideoEl.srcObject) {
          localVideoEl.srcObject = localStream;
        }
      } catch (e) {
        log("Mic denied; will receive-only audio.");
      }
    }
  }

  function setMicEnabled(enabled) {
    if (!localStream) return false;
    localStream.getAudioTracks().forEach((t) => {
      t.enabled = enabled;
    });
    return isMicLive();
  }

  function getAudioSender() {
    if (!pc) return null;
    const withTrack = pc
      .getSenders()
      .find((s) => s.track && s.track.kind === "audio");
    if (withTrack) return withTrack;
    const tr = pc
      .getTransceivers()
      .find(
        (t) =>
          t.sender?.track?.kind === "audio" ||
          t.receiver?.track?.kind === "audio"
      );
    return tr?.sender || null;
  }

  function getVideoSender() {
    if (!pc) return null;
    const withTrack = pc
      .getSenders()
      .find((s) => s.track && s.track.kind === "video");
    if (withTrack) return withTrack;
    const tr = pc
      .getTransceivers()
      .find(
        (t) =>
          t.sender?.track?.kind === "video" ||
          t.receiver?.track?.kind === "video"
      );
    return tr?.sender || null;
  }

  async function applyAudioSenderMax(kbps = currentAudioKbps) {
    const s = getAudioSender();
    if (!s) return;
    const v = Number(kbps);
    if (!Number.isFinite(v) || v <= 0) return;
    try {
      const p = s.getParameters() || {};
      if (!p.encodings || p.encodings.length === 0) p.encodings = [{}];
      p.encodings[0].maxBitrate = Math.round(v * 1000);
      delete p.encodings[0].scaleResolutionDownBy;
      delete p.encodings[0].maxFramerate;
      await s.setParameters(p);
      log(`Applied audio sender maxBitrate=${v} kbps`);
    } catch (e) {
      log(
        "applyAudioSenderMax: setParameters rejected; using SDP only. Reason:",
        e?.message || e
      );
    }
  }

  async function applyVideoSenderMax(kbps = currentVideoKbps) {
    const s = getVideoSender();
    if (!s) return;
    const v = Number(kbps);
    if (!Number.isFinite(v) || v <= 0) return;
    try {
      const p = s.getParameters() || {};
      if (!p.encodings || p.encodings.length === 0) p.encodings = [{}];
      p.encodings[0].maxBitrate = Math.round(v * 1000);
      await s.setParameters(p);
      log(`Applied video sender maxBitrate=${v} kbps`);
    } catch (e) {
      log(
        "applyVideoSenderMax: setParameters rejected. Reason:",
        e?.message || e
      );
    }
  }

  async function setLocalOffer(offerDesc) {
    const mungedSdp = mungeOpusMaxAvgBitrate(offerDesc.sdp, currentAudioKbps);
    const offer = new RTCSessionDescription({
      type: offerDesc.type,
      sdp: mungedSdp,
    });
    await pc.setLocalDescription(offer);
    await applyAudioSenderMax(currentAudioKbps);
    await applyVideoSenderMax(currentVideoKbps);
  }

  async function setLocalAnswer(answerDesc) {
    const mungedSdp = mungeOpusMaxAvgBitrate(answerDesc.sdp, currentAudioKbps);
    const answer = new RTCSessionDescription({
      type: answerDesc.type,
      sdp: mungedSdp,
    });
    await pc.setLocalDescription(answer);
    await applyAudioSenderMax(currentAudioKbps);
    await applyVideoSenderMax(currentVideoKbps);
  }

  async function renegotiate(reason) {
    const remotePeerId = getRemotePeerId();
    if (!pc || !remotePeerId) return;
    if (pc.signalingState !== "stable") {
      log(`Skip renegotiate (${reason}); signalingState=${pc.signalingState}`);
      return;
    }
    try {
      makingOffer = true;
      const offer0 = await pc.createOffer();
      if (pc.signalingState !== "stable") return;
      await setLocalOffer(offer0);
      sendSignal(remotePeerId, { type: "offer", sdp: pc.localDescription });
      log(`Renegotiation (${reason}) offer sent`);
    } catch (e) {
      log("Renegotiation error:", e?.message || String(e));
    } finally {
      makingOffer = false;
    }
  }

  async function setAudioBitrate(kbps, { renegotiateIfLive = true } = {}) {
    const v = Number(kbps);
    if (!Number.isFinite(v) || v <= 0) return currentAudioKbps;
    currentAudioKbps = v;
    await applyAudioSenderMax(v);
    const remotePeerId = getRemotePeerId();
    if (
      renegotiateIfLive &&
      pc &&
      remotePeerId &&
      pc.connectionState !== "closed" &&
      pc.signalingState === "stable"
    ) {
      await renegotiate("audio-bitrate");
    }
    log(`Audio bitrate set to ${v} kbps`);
    return currentAudioKbps;
  }

  async function setVideoBitrate(kbps, { renegotiateIfLive = false } = {}) {
    const v = Number(kbps);
    if (!Number.isFinite(v) || v <= 0) return currentVideoKbps;
    currentVideoKbps = v;
    await applyVideoSenderMax(v);
    const remotePeerId = getRemotePeerId();
    if (
      renegotiateIfLive &&
      pc &&
      remotePeerId &&
      pc.connectionState !== "closed" &&
      pc.signalingState === "stable"
    ) {
      await renegotiate("video-bitrate");
    }
    log(`Video bitrate set to ${v} kbps`);
    return currentVideoKbps;
  }

  async function setMediaBitrates({ audioKbps, videoKbps } = {}) {
    if (audioKbps != null) await setAudioBitrate(audioKbps, { renegotiateIfLive: false });
    if (videoKbps != null) await setVideoBitrate(videoKbps, { renegotiateIfLive: false });
    const remotePeerId = getRemotePeerId();
    if (
      pc &&
      remotePeerId &&
      pc.connectionState !== "closed" &&
      pc.signalingState === "stable" &&
      audioKbps != null
    ) {
      await renegotiate("media-policy");
    } else if (videoKbps != null) {
      await applyVideoSenderMax(currentVideoKbps);
    }
  }

  async function startCamera(localVideoEl) {
    if (!localStream) localStream = new MediaStream();
    if (localStream.getVideoTracks().length === 0) {
      const cam = await navigator.mediaDevices.getUserMedia({
        video: DEFAULT_VIDEO_CONSTRAINTS,
        audio: false,
      });
      cam.getVideoTracks().forEach((t) => localStream.addTrack(t));
      if (pc) {
        cam.getVideoTracks().forEach((t) => pc.addTrack(t, localStream));
        await applyVideoSenderMax(currentVideoKbps);
      }
    } else {
      localStream.getVideoTracks().forEach((t) => {
        t.enabled = true;
      });
    }
    if (localVideoEl) localVideoEl.srcObject = localStream;
    const remotePeerId = getRemotePeerId();
    if (pc && remotePeerId) await renegotiate("video-added");
  }

  async function stopCamera(localVideoEl, { hard = false } = {}) {
    if (!localStream) return;
    const v = localStream.getVideoTracks();
    if (!hard) {
      v.forEach((t) => {
        t.enabled = false;
      });
      if (localVideoEl) localVideoEl.srcObject = localStream;
      const remotePeerId = getRemotePeerId();
      if (pc && remotePeerId) await renegotiate("video-muted");
      return;
    }
    v.forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
      localStream.removeTrack(t);
    });
    if (pc) {
      pc.getSenders()
        .filter((s) => s.track && s.track.kind === "video")
        .forEach((s) => {
          try {
            pc.removeTrack(s);
          } catch {
            /* ignore */
          }
        });
    }
    if (localStream.getTracks().length === 0) {
      if (localVideoEl) localVideoEl.srcObject = null;
      localStream = null;
    } else if (localVideoEl) {
      localVideoEl.srcObject = localStream;
    }
    const remotePeerId = getRemotePeerId();
    if (pc && remotePeerId) await renegotiate("video-removed");
  }

  async function toggleCamera(localVideoEl) {
    if (isCameraOn()) return stopCamera(localVideoEl, { hard: false });
    return startCamera(localVideoEl);
  }

  async function startCall() {
    const remotePeerId = getRemotePeerId();
    if (!remotePeerId) {
      log("No remotePeerId chosen yet.");
      return;
    }
    await ensurePC();
    await ensureMic();
    if (pc.signalingState !== "stable") {
      log(`Cannot startCall; signalingState=${pc.signalingState}`);
      return;
    }
    try {
      makingOffer = true;
      const offer0 = await pc.createOffer();
      await setLocalOffer(offer0);
      sendSignal(remotePeerId, { type: "offer", sdp: pc.localDescription });
      log("Sent offer to", remotePeerId, `(audio ${currentAudioKbps} kbps)`);
    } finally {
      makingOffer = false;
    }
  }

  async function handleSignal(from, payload) {
    if (!pc) await ensurePC();

    if (payload.type === "offer") {
      const polite = isPolite();
      const offerCollision =
        makingOffer || pc.signalingState !== "stable";
      if (offerCollision) {
        if (!polite) {
          log("Glare: ignoring remote offer (impolite)");
          return;
        }
        log("Glare: rolling back local offer (polite)");
        await Promise.all([
          pc.setLocalDescription({ type: "rollback" }),
          pc.setRemoteDescription(new RTCSessionDescription(payload.sdp)),
        ]);
      } else {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      }
      remoteDescSet = true;
      await flushCandidates();
      await ensureMic();
      const answer0 = await pc.createAnswer();
      await setLocalAnswer(answer0);
      sendSignal(from, { type: "answer", sdp: pc.localDescription });
      log("Sent answer to", from, `(audio ${currentAudioKbps} kbps)`);
    } else if (payload.type === "answer") {
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      remoteDescSet = true;
      await flushCandidates();
      log("Applied remote answer");
    } else if (payload.type === "candidate") {
      const cand = payload.candidate;
      if (!cand) return;
      const ice = new RTCIceCandidate(cand);
      if (!remoteDescSet) {
        pendingCandidates.push(ice);
        return;
      }
      try {
        await pc.addIceCandidate(ice);
      } catch (e) {
        log("addIceCandidate error", e.toString());
      }
    }
  }

  /** Tear down PC only; keep local media tracks for preview. */
  function closePeerConnection(remoteVideoEl) {
    try {
      pc?.close();
    } catch {
      /* ignore */
    }
    pc = null;
    dc = null;
    pendingCandidates = [];
    remoteDescSet = false;
    makingOffer = false;
    if (remoteVideoEl) remoteVideoEl.srcObject = null;
  }

  /** Full hangup: stop tracks + PC. */
  function close(localVideoEl, remoteVideoEl) {
    closePeerConnection(remoteVideoEl);
    try {
      localStream?.getTracks()?.forEach((t) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
    } catch {
      /* ignore */
    }
    localStream = null;
    if (localVideoEl) localVideoEl.srcObject = null;
  }

  return {
    ensurePC,
    ensureMic,
    startCall,
    handleSignal,
    toggleCamera,
    setMicEnabled,
    setAudioBitrate,
    setVideoBitrate,
    setMediaBitrates,
    applyAudioSenderMax,
    applyVideoSenderMax,
    isCameraOn,
    isMicLive,
    isPeerActive,
    closePeerConnection,
    close,
    get audioBitrateKbps() {
      return currentAudioKbps;
    },
    get videoBitrateKbps() {
      return currentVideoKbps;
    },
    get localStream() {
      return localStream;
    },
    get pc() {
      return pc;
    },
  };
}
